import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Divider,
  Input,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  FontColorsOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

const { Text, Link } = Typography;
const { Panel } = Collapse;

// Popular web-safe fonts
const POPULAR_FONTS = [
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Palatino", value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" },
  { label: "Garamond", value: "Garamond, serif" },
  { label: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
  { label: "Impact", value: "Impact, Charcoal, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
];

export type FontConfig = {
  fontFamily?: string;
  googleFontsUrl?: string;
};

export type ModuleFonts = {
  main?: FontConfig;
  subsections?: {
    [key: string]: FontConfig;
  };
};

export type CompanyFonts = {
  general?: FontConfig;
  kanban?: ModuleFonts;
  documentation?: ModuleFonts;
  calendar?: ModuleFonts;
  tasks?: ModuleFonts;
  profiles?: ModuleFonts;
  email?: ModuleFonts;
  reports?: ModuleFonts;
};

interface FontSettingsProps {
  value?: CompanyFonts;
  onChange?: (value: CompanyFonts) => void;
}

type SubsectionDef = { key: string; label: string };

type ModuleDef = {
  key: keyof Omit<CompanyFonts, "general">;
  label: string;
  icon: string;
  subsections?: SubsectionDef[];
};

const MODULES: ModuleDef[] = [
  {
    key: "kanban",
    label: "Kanban Board",
    icon: "📋",
    subsections: [
      { key: "cards", label: "Task Cards" },
      { key: "headers", label: "Headers" },
    ],
  },
  {
    key: "documentation",
    label: "Documentation",
    icon: "📄",
    subsections: [
      { key: "text", label: "Document Text" },
      { key: "files", label: "File Names" },
    ],
  },
  {
    key: "calendar",
    label: "Calendar",
    icon: "📅",
    subsections: [
      { key: "events", label: "Events" },
      { key: "notes", label: "Notes" },
    ],
  },
  {
    key: "tasks",
    label: "Tasks",
    icon: "✓"

  },
  {
    key: "profiles",
    label: "User Profiles",
    icon: "👤",
    subsections: [
      { key: "names", label: "User Names" },
      { key: "descriptions", label: "Descriptions" },
    ],
  },
  {
    key: "email",
    label: "Email Templates",
    icon: "✉️"
  },
  {
    key: "reports",
    label: "Reports",
    icon: "📊"
  },
];

const DEFAULT_PREVIEW = "The quick brown fox jumps over the lazy dog";

const FontSettings: React.FC<FontSettingsProps> = ({ value = {}, onChange }) => {
  const [previewText, setPreviewText] = useState(DEFAULT_PREVIEW);
  const [expandedKeys, setExpandedKeys] = useState<string[]>(["general"]);

  // Load Google Fonts
  useEffect(() => {
    const existingLinks = document.querySelectorAll('link[data-font-preview="true"]');
    existingLinks.forEach((link) => link.remove());

    const loadedUrls = new Set<string>();
    
    // General font
    if (value.general?.googleFontsUrl && !loadedUrls.has(value.general.googleFontsUrl)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = value.general.googleFontsUrl;
      link.setAttribute("data-font-preview", "true");
      document.head.appendChild(link);
      loadedUrls.add(value.general.googleFontsUrl);
    }

    // Module fonts
    MODULES.forEach(({ key }) => {
      const module = value[key];
      if (module?.main?.googleFontsUrl && !loadedUrls.has(module.main.googleFontsUrl)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = module.main.googleFontsUrl;
        link.setAttribute("data-font-preview", "true");
        document.head.appendChild(link);
        loadedUrls.add(module.main.googleFontsUrl);
      }

      // Subsection fonts
      if (module?.subsections) {
        Object.values(module.subsections).forEach((subsection) => {
          if (subsection?.googleFontsUrl && !loadedUrls.has(subsection.googleFontsUrl)) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = subsection.googleFontsUrl;
            link.setAttribute("data-font-preview", "true");
            document.head.appendChild(link);
            loadedUrls.add(subsection.googleFontsUrl);
          }
        });
      }
    });

    return () => {
      const links = document.querySelectorAll('link[data-font-preview="true"]');
      links.forEach((link) => link.remove());
    };
  }, [value]);

  const parseGoogleFontsUrl = (url: string): { fontFamily: string } | null => {
    try {
      const urlObj = new URL(url);
      const familyParam = urlObj.searchParams.get("family");
      if (!familyParam) return null;

      const parts = familyParam.split("&")[0].split(":");
      const fontFamily = parts[0]?.replace(/\+/g, " ") || "";
      return { fontFamily };
    } catch {
      return null;
    }
  };

  const setGeneralFont = (field: keyof FontConfig, fieldValue: string) => {
    const next: CompanyFonts = { ...value };
    const current = { ...(next.general ?? {}) };

    if (field === "googleFontsUrl" && fieldValue) {
      const parsed = parseGoogleFontsUrl(fieldValue);
      if (parsed) {
        current.googleFontsUrl = fieldValue;
        current.fontFamily = parsed.fontFamily;
      } else {
        current.googleFontsUrl = fieldValue;
      }
    } else {
      (current as any)[field] = fieldValue;
    }

    next.general = current;
    onChange?.(next);
  };

  const setModuleMainFont = (
    moduleKey: keyof Omit<CompanyFonts, "general">,
    field: keyof FontConfig,
    fieldValue: string
  ) => {
    const next: CompanyFonts = { ...value };
    const module = { ...(next[moduleKey] ?? {}) };
    const current = { ...(module.main ?? {}) };

    if (field === "googleFontsUrl" && fieldValue) {
      const parsed = parseGoogleFontsUrl(fieldValue);
      if (parsed) {
        current.googleFontsUrl = fieldValue;
        current.fontFamily = parsed.fontFamily;
      } else {
        current.googleFontsUrl = fieldValue;
      }
    } else {
      (current as any)[field] = fieldValue;
    }

    module.main = current;
    next[moduleKey] = module;
    onChange?.(next);
  };

  const setSubsectionFont = (
    moduleKey: keyof Omit<CompanyFonts, "general">,
    subsectionKey: string,
    field: keyof FontConfig,
    fieldValue: string
  ) => {
    const next: CompanyFonts = { ...value };
    const module = { ...(next[moduleKey] ?? {}) };
    const subsections = { ...(module.subsections ?? {}) };
    const current = { ...(subsections[subsectionKey] ?? {}) };

    if (field === "googleFontsUrl" && fieldValue) {
      const parsed = parseGoogleFontsUrl(fieldValue);
      if (parsed) {
        current.googleFontsUrl = fieldValue;
        current.fontFamily = parsed.fontFamily;
      } else {
        current.googleFontsUrl = fieldValue;
      }
    } else {
      (current as any)[field] = fieldValue;
    }

    subsections[subsectionKey] = current;
    module.subsections = subsections;
    next[moduleKey] = module;
    onChange?.(next);
  };

  const clearGeneral = () => {
    const next: CompanyFonts = { ...value };
    next.general = undefined;
    onChange?.(next);
  };

  const clearModule = (moduleKey: keyof Omit<CompanyFonts, "general">) => {
    const next: CompanyFonts = { ...value };
    delete next[moduleKey];
    onChange?.(next);
  };

  const clearSubsection = (
    moduleKey: keyof Omit<CompanyFonts, "general">,
    subsectionKey: string
  ) => {
    const next: CompanyFonts = { ...value };
    const module = { ...(next[moduleKey] ?? {}) };
    const subsections = { ...(module.subsections ?? {}) };
    delete subsections[subsectionKey];
    module.subsections = subsections;
    next[moduleKey] = module;
    onChange?.(next);
  };

  const toggleModuleOverride = (
    moduleKey: keyof Omit<CompanyFonts, "general">,
    enabled: boolean
  ) => {
    const next: CompanyFonts = { ...value };
    if (enabled) {
      next[moduleKey] = next[moduleKey] ?? {};
    } else {
      delete next[moduleKey];
    }
    onChange?.(next);
  };

  const isModuleOverridden = (moduleKey: keyof Omit<CompanyFonts, "general">) => {
    return !!value[moduleKey];
  };

  const getEffectiveMainFont = (moduleKey: keyof Omit<CompanyFonts, "general">) => {
    return value[moduleKey]?.main ?? value.general;
  };

  const getEffectiveSubsectionFont = (
    moduleKey: keyof Omit<CompanyFonts, "general">,
    subsectionKey: string
  ) => {
    return (
      value[moduleKey]?.subsections?.[subsectionKey] ??
      value[moduleKey]?.main ??
      value.general
    );
  };

  const renderPreview = (cfg?: FontConfig, small?: boolean) => {
    const fontFamily = cfg?.fontFamily?.trim();
    if (!fontFamily) return <Text type="secondary">No font</Text>;

    if (small) {
      return (
        <Text style={{ fontFamily, fontSize: 13, whiteSpace: "nowrap" }}>
          {fontFamily}
        </Text>
      );
    }

    return (
      <div
        style={{
          marginTop: 8,
          padding: 12,
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          fontFamily,
          fontSize: 16,
        }}
      >
        {previewText}
      </div>
    );
  };

  const renderFontInputs = (
    fontConfig: FontConfig | undefined,
    onFieldChange: (field: keyof FontConfig, value: string) => void,
    disabled: boolean = false
  ) => (
    <Space direction="vertical" style={{ width: "100%" }} size="small">
      <Select
        placeholder="Select popular font or use Google Fonts"
        value={fontConfig?.fontFamily || undefined}
        onChange={(val) => onFieldChange("fontFamily", val)}
        disabled={disabled}
        allowClear
        showSearch
        style={{ width: "100%" }}
        options={POPULAR_FONTS}
      />

      <Input
        placeholder="Or paste Google Fonts URL"
        value={fontConfig?.googleFontsUrl || ""}
        onChange={(e) => onFieldChange("googleFontsUrl", e.target.value)}
        addonBefore="Google URL"
        disabled={disabled}
      />

      <Input
        placeholder="Font Family (auto-filled or custom)"
        value={fontConfig?.fontFamily || ""}
        onChange={(e) => onFieldChange("fontFamily", e.target.value)}
        addonBefore="Family"
        disabled={disabled}
      />
    </Space>
  );

  const allKeys = ["general", ...MODULES.map((m) => String(m.key))];
  const expandAll = () => setExpandedKeys(allKeys);
  const collapseAll = () => setExpandedKeys([]);

  return (
    <div>
      <Alert
        message="Font Settings Guide"
        description={
          <div>
            <p>
              <strong>Quick Setup:</strong> Select a popular font from the dropdown, or use
              custom fonts from Google Fonts:
            </p>
            <ol>
              <li>
                Visit{" "}
                <Link href="https://fonts.google.com" target="_blank">
                  Google Fonts
                </Link>
              </li>
              <li>Select a font and click "Get font" → "Get embed code"</li>
              <li>
                Copy the URL from the <code>&lt;link&gt;</code> tag
              </li>
              <li>Paste it in "Google Fonts URL"</li>
            </ol>
            <p>
              <strong>Note:</strong> <em>General</em> applies to the whole app. Each module
              can have its own font, and subsections within modules can also be customized.
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Input
        placeholder="Preview text…"
        value={previewText}
        onChange={(e) => setPreviewText(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<FontColorsOutlined />}
      />

      <Space style={{ marginBottom: 16 }}>
        <Button onClick={expandAll} icon={<PlusOutlined />} size="small">
          Expand all
        </Button>
        <Button onClick={collapseAll} icon={<UnorderedListOutlined />} size="small">
          Collapse all
        </Button>
      </Space>

      <Collapse
        activeKey={expandedKeys}
        onChange={(keys) => setExpandedKeys(keys as string[])}
        expandIconPosition="end"
      >
        {/* General Settings */}
        <Panel
          key="general"
          header={
            <Space size={10} wrap>
              <span>🌐</span>
              <Text strong>General (All Application)</Text>
              {value.general ? (
                <Tag color="blue">Active</Tag>
              ) : (
                <Tag>Unset</Tag>
              )}
              <span style={{ opacity: 0.6 }}>
                {renderPreview(value.general, true)}
              </span>
            </Space>
          }
          extra={
            value.general && (
              <Tooltip title="Clear General">
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearGeneral();
                  }}
                />
              </Tooltip>
            )
          }
        >
          {renderFontInputs(value.general, setGeneralFont)}
          {renderPreview(value.general)}
        </Panel>

        {/* Module Settings */}
        {MODULES.map((module) => {
          const overridden = isModuleOverridden(module.key);
          const mainFont = getEffectiveMainFont(module.key);
          const disabledInputs = !overridden;

          return (
            <Panel
              key={module.key}
              header={
                <Space size={10} wrap>
                  <span>{module.icon}</span>
                  <Text>{module.label}</Text>
                  {overridden ? (
                    <Tag color="green">Custom</Tag>
                  ) : (
                    <Tag>Inherited</Tag>
                  )}
                  <span style={{ opacity: 0.6 }}>
                    {renderPreview(mainFont, true)}
                  </span>
                </Space>
              }
              extra={
                <Space size={8} onClick={(e) => e.stopPropagation()}>
                  <Space size={6}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Override
                    </Text>
                    <Switch
                      size="small"
                      checked={overridden}
                      onChange={(val) => toggleModuleOverride(module.key, val)}
                    />
                  </Space>
                  {overridden && (
                    <Tooltip title="Remove Override">
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => clearModule(module.key)}
                      />
                    </Tooltip>
                  )}
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                {/* Main module font */}
                <Card
                  size="small"
                  title={<Text strong>Main Font</Text>}
                  type="inner"
                >
                  {renderFontInputs(
                    value[module.key]?.main,
                    (field, val) => setModuleMainFont(module.key, field, val),
                    disabledInputs
                  )}
                  {renderPreview(mainFont)}
                </Card>

                {/* Subsections */}
                { module.subsections && 
                                <Card
                  size="small"
                  title={<Text strong>Subsections</Text>}
                  type="inner"
                >
                  <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    {module.subsections?.map((subsection) => {
                      const subsectionFont = getEffectiveSubsectionFont(
                        module.key,
                        subsection.key
                      );
                      const hasCustom = !!value[module.key]?.subsections?.[subsection.key];

                      return (
                        <Card
                          key={subsection.key}
                          size="small"
                          title={
                            <Space>
                              <Text>{subsection.label}</Text>
                              {hasCustom ? (
                                <Tag color="orange" style={{ fontSize: 11 }}>
                                  Custom
                                </Tag>
                              ) : (
                                <Tag style={{ fontSize: 11 }}>Inherited</Tag>
                              )}
                            </Space>
                          }
                          extra={
                            hasCustom && (
                              <Button
                                danger
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  clearSubsection(module.key, subsection.key)
                                }
                              />
                            )
                          }
                        >
                          {renderFontInputs(
                            value[module.key]?.subsections?.[subsection.key],
                            (field, val) =>
                              setSubsectionFont(module.key, subsection.key, field, val),
                            disabledInputs
                          )}
                          {renderPreview(subsectionFont)}
                        </Card>
                      );
                    })}
                  </Space>
                </Card>}
              </Space>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};

export default FontSettings;